import { pickBy, identity } from 'lodash';

import { httpV1 as http } from '@/services/api';

const ALT_API_URL = `${process.env.VUE_APP_ALT_BASE_API_URL || process.env.VUE_APP_BASE_API_URL}/v1`;

export async function getBasics({ account_id, params }) {
  const response = await http.get(`/clients/${account_id}`, { params });
  return response;
}

export async function getDocument(accountId, qualificationId) {
  const response = await http.get(`/clients/${accountId}/qualification/${qualificationId}`);
  return response;
}

export async function getAddress(accountId, addressId) {
  const response = await http.get(`/clients/${accountId}/address/${addressId}`);
  return response;
}

export async function getIncome(accountId, professionalId) {
  const response = await http.get(`/clients/${accountId}/professional/${professionalId}`);
  return response;
}

export async function getBankAccount(accountId, bankAccountId) {
  const response = await http.get(`/clients/${accountId}/bank-account/${bankAccountId}`);
  return response;
}

export async function updateBasics(accountId, params) {
  const { data } = await http.put(`/clients/${accountId}`, params);
  return data;
}

export async function createDocument(params) {
  const { data } = await http.post('/clients/qualification/admin', params);
  return data;
}

export async function updateDocument(accountId, qualificationId, params) {
  const { data } = await http.put(`/clients/${accountId}/qualification/${qualificationId}`, params);
  return data;
}

export async function createAddress(params) {
  const { data } = await http.post('/clients/address/admin', params);
  return data;
}

export async function updateAddress(accountId, addressId, params) {
  const { data } = await http.put(`/clients/${accountId}/address/${addressId}`, params);
  return data;
}

export async function getProfessionalList(params) {
  const { data } = await http.get('/clients/professions/list', { params });
  return data;
}

export async function createIncome(params) {
  const { data } = await http.post('/clients/professional/admin', params);
  return data;
}

export async function updateIncome(accountId, professionalId, params) {
  const { data } = await http.put(`/clients/${accountId}/professional/${professionalId}`, params);
  return data;
}

export async function createBankAccount(params) {
  const { data } = await http.post('/clients/bank-account/admin', params);
  return data;
}

export async function updateBankAccount(accountId, bankAccountId, params) {
  const { data } = await http.put(`${ALT_API_URL}/clients/${accountId}/bank-account/${bankAccountId}`, params);
  return data;
}

export async function createBasic(data) {
  const { data: _data } = await http.post('/clients/account/admin', data);
  return _data;
}

export async function checkUniqueAccount({ document, ...params }) {
  return http.get(`/clients/account/${document}`, { params });
}

export async function getClientData(document) {
  const { data } = await http.get('/cgv/credit-check-consent', { params: { document } });
  return data;
}

export async function getProfessionCategoryList(params) {
  const { data } = await http.get('/clients/profession-categories/list', { params });
  return data;
}

/**
 * Get the corban screening (pre-cadastro) list
 * @function getCorbanScreeningList
 * @param {object} params - Filter params: name, begin_date, end_date, status,
 *  commercial_id, page, per_page. Falsy values are stripped before the request.
 * @returns {any}
 */
export async function getCorbanScreeningList(params = {}) {
  const { data } = await http.get('/clients/corban-prospection', { params: pickBy(params, identity) });
  return data;
}

/**
 * Get the full compliance detail of a single corban screening (pre-cadastro).
 * Returns `{ prospection, steps, decision }` — `prospection` is the raw
 * `corban_prospection` row, `steps` is the full `corban_registration_history`
 * timeline (the ownership structure, automated checks and rules engine views
 * are all derived from it front-side, see `data/corbanRegistrationHistorySteps.js`),
 * and `decision` is the same `decision` step already singled out.
 * @function getCorbanScreeningDetails
 * @param {number} prospectionId
 * @returns {any}
 */
export async function getCorbanScreeningDetails(prospectionId) {
  const { data } = await http.get(`/clients/corban-prospection/${prospectionId}/details`);
  return data;
}

/**
 * Redo a failed CredLocaliza check; answers 200 even when the provider fails again (see `status`).
 * The provider can take up to 180s, so the default timeout is lifted for this call.
 * @function retryCorbanCredLocaliza
 * @param {number} prospectionId
 * @param {'company'|'responsavel'} subject
 * @returns {Promise<{ data: { status, result, error, createdAt } }>} status: success or failed
 */
export async function retryCorbanCredLocaliza(prospectionId, subject) {
  const { data } = await http.post(
    `/clients/${prospectionId}/credlocaliza/retry`,
    { subject },
    { timeout: 200000 },
  );
  return data;
}

/**
 * Set or change the commercial responsible for a corban screening.
 * Answers 403/404/409/422 when the user cannot edit it or the screening is already closed.
 * @function setCorbanScreeningCommercial
 * @param {number} prospectionId
 * @param {number} commercialId
 * @returns {any}
 */
export async function setCorbanScreeningCommercial(prospectionId, commercialId) {
  const { data } = await http.put(`/clients/corban-prospection/${prospectionId}/commercial`, { commercial_id: commercialId });
  return data;
}

/**
 * Create a corban screening from the admin; the backend starts the registration workflow.
 * @function createCorbanScreening
 * @param {object} payload - the institutional site's fields, plus the optional contract data
 * @returns {any}
 */
export async function createCorbanScreening(payload) {
  const { data } = await http.post('/clients/corban-prospection/admin', payload);
  return data;
}

/**
 * Upload one of the two contracts awaited after approval (multipart, PDF in `file`); each one
 * sends its own workflow signal, unlike `attachCorbanDocument`.
 * @function uploadCorbanContract
 * @param {number} prospectionId
 * @param {'service-agreement'|'social-contract'} contract
 * @param {FormData} formData
 * @returns {any}
 */
export async function uploadCorbanContract(prospectionId, contract, formData) {
  const { data } = await http.post(`/clients/${prospectionId}/contracts/${contract}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Save the contract data (upsert). While `aguardando-dados-contrato`, a complete set also
 * signals the workflow to generate the contract; an incomplete one is saved and answered with 422.
 * @function saveCorbanContractData
 * @param {number} prospectionId
 * @param {object} payload - see `buildContractDataUpdate`
 * @returns {any}
 */
export async function saveCorbanContractData(prospectionId, payload) {
  const { data } = await http.put(`/clients/${prospectionId}/contract-data`, payload);
  return data;
}

/**
 * The generated service agreement PDF, as a Blob (the route needs the bearer, so no plain link).
 * @function getCorbanGeneratedServiceAgreement
 * @param {number} prospectionId
 * @returns {Promise<Blob>}
 */
export async function getCorbanGeneratedServiceAgreement(prospectionId) {
  const { data } = await http.get(`/clients/${prospectionId}/contracts/service-agreement/file`, {
    responseType: 'blob',
  });
  return data;
}

/**
 * Every ClickSign send of the service agreement, resends included.
 * @function getCorbanContractSignatures
 * @param {number} prospectionId
 * @returns {Promise<{ data: Array<object> }>} status, sentAt, expiresAt, signedAt, error, resentBy
 */
export async function getCorbanContractSignatures(prospectionId) {
  const { data } = await http.get(`/clients/${prospectionId}/contracts/signatures`);
  return data;
}

/**
 * Cancel the current ClickSign send of the service agreement and send a new one.
 * @function resendCorbanContractSignature
 * @param {number} prospectionId
 * @returns {Promise<{ data: object }>} the new `serviceContractSignature`
 */
export async function resendCorbanContractSignature(prospectionId) {
  const { data } = await http.post(`/clients/${prospectionId}/contracts/signatures/resend`);
  return data;
}

/**
 * E-mail the partner a reminder of the open ClickSign send, keeping its envelope.
 * @function remindCorbanContractSignature
 * @param {number} prospectionId
 * @param {number} signatureId the open send
 * @returns {Promise<{ data: object }>} the updated `serviceContractSignature`, with `remindedAt`
 */
export async function remindCorbanContractSignature(prospectionId, signatureId) {
  const { data } = await http.post(`/clients/${prospectionId}/contracts/signatures/${signatureId}/remind`);
  return data;
}

/**
 * The signed service agreement PDF, as a Blob (`signedFileUrl` needs the bearer, so no plain link).
 * @function getCorbanSignedServiceAgreement
 * @param {string} signedFileUrl - from `serviceContractSignature`
 * @returns {Promise<Blob>}
 */
export async function getCorbanSignedServiceAgreement(signedFileUrl) {
  // It arrives as `/v1/clients/...`, and `http` already points at `/v1`.
  const { data } = await http.get(signedFileUrl.replace(/^\/v1(?=\/)/, ''), { responseType: 'blob' });
  return data;
}

/**
 * Screenings still in progress with the same CPF or CNPJ.
 * @function getCorbanScreeningDuplicates
 * @param {{ document: string, company_document?: string }} params
 * @returns {any}
 */
export async function getCorbanScreeningDuplicates(params) {
  const { data } = await http.get('/clients/corban-prospection/duplicates', { params: pickBy(params, identity) });
  return data;
}

/**
 * Apply the real comercial-decision workflow signal to a corban screening
 * (aprovar/reprovar) via `POST /v1/clients/{id}/decision`; the user comes from the session.
 * Distinct from the old `/clients/corban-prospection/{id}` PUT, which only patches the row.
 * @function decideCorbanScreening
 * @param {number} prospectionId
 * @param {'aprovado'|'reprovado'} decision
 * @param {string} justification
 * @returns {any}
 */
export async function decideCorbanScreening(prospectionId, decision, justification) {
  const { data } = await http.post(`/clients/${prospectionId}/decision`, { decision, justification });
  return data;
}

/**
 * Manually attach a document to a corban prospection (multipart), for cases
 * where the corban sent it outside the app. Records a `document-attached`
 * history step.
 * @function attachCorbanDocument
 * @param {number} prospectionId
 * @param {FormData} formData - fields: file, documentType, observation?
 * @returns {any}
 */
export async function attachCorbanDocument(prospectionId, formData) {
  const { data } = await http.post(`/clients/${prospectionId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Request a document from the corban (email notification — whatsapp isn't
 * available yet, see docs/pendencias-front.md). Records a
 * `document-requested` history step.
 * @function requestCorbanDocument
 * @param {number} prospectionId
 * @param {object} payload - documentType, reason, dueDate, notifyChannels, observation?, userId
 * @returns {any}
 */
export async function requestCorbanDocument(prospectionId, payload) {
  const { data } = await http.post(`/clients/${prospectionId}/documents/request`, payload);
  return data;
}

/**
 * Request free-form information from the corban (email notification —
 * whatsapp isn't available yet, see docs/pendencias-front.md). Records an
 * `information-requested` history step.
 * @function requestCorbanInformation
 * @param {number} prospectionId
 * @param {object} payload - reason, dueDate, notifyChannels, observation?, userId
 * @returns {any}
 */
export async function requestCorbanInformation(prospectionId, payload) {
  const { data } = await http.post(`/clients/${prospectionId}/information/request`, payload);
  return data;
}

/**
 * Manually flag a corban prospection back to commercial review, without
 * re-running any check (`aguardando-contratos`/`concluido` only, 422
 * otherwise). Records an `analysis-reopened` history step.
 * @function reopenCorbanAnalysis
 * @param {number} prospectionId
 * @param {string} justification
 * @param {number} userId
 * @returns {any}
 */
export async function reopenCorbanAnalysis(prospectionId, justification, userId) {
  const { data } = await http.post(`/clients/${prospectionId}/analysis/reopen`, {
    justification, userId,
  });
  return data;
}

/**
 * Manually flag a reproved corban prospection back to commercial review, as
 * a commercial exception (`reprovado` only, 422 otherwise). Records a
 * `reconsideration-requested` history step.
 * @function requestCorbanReconsideration
 * @param {number} prospectionId
 * @param {object} payload - requestedBy, reason, justification, userId
 * @returns {any}
 */
export async function requestCorbanReconsideration(prospectionId, payload) {
  const { data } = await http.post(`/clients/${prospectionId}/reconsideration/request`, payload);
  return data;
}

export async function searchClients(params) {
  const { data } = await http.get('/clients/search', { params: pickBy(params, identity) });
  return data;
}
